import React from "react";
import { motion } from "framer-motion";
import { T } from "@/context/LanguageContext";

const PRINCIPLES = [
  { t: "Geographic-first", d: "Every place is a canonical entity with a permanent ID, official codes and relationships — not just a URL." },
  { t: "Data-first", d: "Structured, sourced data comes before AI prose. The page is only the view; the data is the product." },
  { t: "Source-first", d: "Important factual claims are traceable — with source, data year and last-verified date." },
  { t: "Multilingual-first", d: "Language is part of the content model. 48+ international and Indian languages, not an afterthought." },
  { t: "Accessibility-first", d: "Listen / text-to-speech and an accessible, RTL-capable interface across the whole platform." },
  { t: "Community-first", d: "Residents can contribute local knowledge, stories and photos — with clear provenance." },
  { t: "AI-assisted, not AI-unchecked", d: "AI researches, translates and maintains — but never has unlimited authority to invent facts." },
  { t: "Wikipedia-aware", d: "Relevant Wikipedia and Wikidata relationships are surfaced as a reference layer, never a replacement." },
  { t: "Interconnected", d: "USAPulse ↔ WeatherPulse ↔ SciencePulse ↔ NaturePulse and future Pulse networks." },
  { t: "Continuously maintained", d: "Every entity carries a freshness and completeness score that guides what to improve next." },
];

export function Manifesto() {
  return (
    <section className="bg-navy-deep py-24 text-white">
      <div className="mx-auto max-w-[1500px] px-4">
        <div className="mb-14 max-w-3xl">
          <div className="mb-3 text-xs font-bold uppercase tracking-[0.25em] text-oldglory-red"><T>The Architecture</T></div>
          <h2 className="font-display text-4xl font-bold leading-tight md:text-5xl">
            <T>Ten principles behind America's living knowledge network</T>
          </h2>
          <p className="mt-4 text-white/60">
            <T>USAPulse is built as one geographic knowledge system capable of generating millions of high-quality experiences — not a million disconnected pages.</T>
          </p>
        </div>

        <div className="grid gap-x-12 gap-y-10 md:grid-cols-2">
          {PRINCIPLES.map((p, i) => (
            <motion.div
              key={p.t}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: (i % 2) * 0.1 }}
              className="group flex gap-6 border-t border-white/10 pt-6"
            >
              <span className="font-display text-4xl font-black text-oldglory-red/80 transition-colors group-hover:text-oldglory-red">
                {String(i + 1).padStart(2, "0")}
              </span>
              <div>
                <h3 className="font-display text-xl font-semibold"><T>{p.t}</T></h3>
                <p className="mt-1.5 text-sm leading-relaxed text-white/60"><T>{p.d}</T></p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
